import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, Empty, Spinner, Stat } from "@/components/ui";
import { api } from "@/lib/api";
import { money } from "@/lib/states";

const DAYS = 30;

/** Days with no spend don't exist in the ledger; a gap-free axis needs them. */
function fillGaps(
  daily: { day: string; costUsd: number }[],
  days: number,
): { day: string; costUsd: number }[] {
  const byDay = new Map(daily.map((d) => [d.day, d.costUsd]));
  const out: { day: string; costUsd: number }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(Date.now() - i * 86400000);
    const key = date.toISOString().slice(0, 10);
    out.push({ day: key, costUsd: byDay.get(key) ?? 0 });
  }
  return out;
}

function Costs() {
  const query = useQuery({
    queryKey: ["costs", DAYS],
    queryFn: () => api.costs(DAYS),
  });
  const overview = useQuery({ queryKey: ["overview"], queryFn: api.overview });
  const metrics = useQuery({
    queryKey: ["metrics", DAYS],
    queryFn: () => api.metrics(DAYS),
  });

  if (query.isPending) return <Spinner />;
  if (query.isError) {
    return <Empty>Couldn't load costs. {query.error.message}</Empty>;
  }

  const series = fillGaps(query.data.daily, DAYS);
  const total = series.reduce((sum, d) => sum + d.costUsd, 0);
  const byModel = query.data.byModel;
  const modelTotal = byModel.reduce((sum, m) => sum + m.costUsd, 0);

  const o = overview.data;
  const m = metrics.data;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6 overflow-y-auto p-6">
      {o && m && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat
            label="Spend today"
            value={money(o.spend.todayUsd)}
            hint={`cap ${money(o.spend.dailyCapUsd)}`}
            tone={o.spend.todayUsd >= o.spend.dailyCapUsd ? "bad" : undefined}
          />
          <Stat label="Live tasks" value={String(o.live.length)} />
          <Stat
            label={`Success rate (${DAYS}d)`}
            value={
              m.successRate === null
                ? "—"
                : `${Math.round(m.successRate * 100)}%`
            }
          />
          <Stat
            label="Cost / merged PR"
            value={money(m.costPerMergedPrUsd)}
            hint={m.spendScope}
          />
        </div>
      )}
      {o?.killSwitch && (
        <p className="rounded-lg border border-bad/40 bg-bad/10 p-3 text-bad text-sm">
          Kill switch is ON — all runs stopped, queue paused.
        </p>
      )}
      <Card
        title={`Daily spend — last ${DAYS} days`}
        action={
          <span className="text-muted text-xs tabular-nums">
            {money(total)} total
          </span>
        }
      >
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ left: -20, top: 8 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--color-edge)"
                vertical={false}
              />
              <XAxis
                dataKey="day"
                tickFormatter={(d: string) => d.slice(5)}
                stroke="var(--color-muted)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis
                stroke="var(--color-muted)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => `$${v}`}
              />
              <Tooltip
                cursor={{ fill: "var(--color-panel)" }}
                contentStyle={{
                  background: "var(--color-panel)",
                  border: "1px solid var(--color-edge)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(v: number) => [money(v), "spend"]}
              />
              <Bar
                dataKey="costUsd"
                fill="var(--color-brand)"
                radius={[3, 3, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="By model">
        {byModel.length === 0 ? (
          <Empty>No spend recorded yet.</Empty>
        ) : (
          <ul className="flex flex-col gap-3">
            {byModel.map((m) => (
              <li key={`${m.model}:${m.source}`}>
                <div className="flex items-baseline justify-between text-sm">
                  <span>
                    {m.model}
                    <span className="ml-2 text-muted text-xs">{m.source}</span>
                  </span>
                  <span className="tabular-nums">{money(m.costUsd)}</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-edge">
                  <div
                    className="h-full rounded-full bg-brand"
                    style={{
                      width: `${modelTotal > 0 ? (m.costUsd / modelTotal) * 100 : 0}%`,
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/costs")({ component: Costs });
